import React, { useState } from "react";
import {
  Grid,
  SegmentedControl,
  Text,
  Button,
  LoadingOverlay,
  Loader,
  Flex,
  Box,
  ThemeIcon,
  Modal,
  Card,
  Badge,
  TextInput,
} from "@mantine/core";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { IconCheck } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { numberFormatter } from "@util/formatters";
import UserGroup, { AddRemoveButtons } from "./UserGroup";
import { Item as UserGroupItem } from "@components/UserGroup/UserGroupList";
import { getEffortUserGroups } from "./helpers";

const addModeInfo = {
  groups: {
    label: "Manage users by segment",
    description: "Segments description",
    value: "user_groups",
    enum: 1,
  },
  all: {
    label: "Manage all users",
    description: "All description",
    value: "all_users",
    enum: 2,
  },
};

export default function UserAdd({
  entityInfo,
  totalPossibleUsers,
  userManagementType,
  fetchData,
}) {
  const [addMode, setAddMode] = useState(
    userManagementType === 1 ? addModeInfo.groups.value : addModeInfo.all.value
  );
  const [loading, setLoading] = useState(false);
  const queryKey = ["user-add-status", entityInfo.bulk_communicator_id];
  const pollingQueryKey = [
    "user-add-status-polling",
    entityInfo.bulk_communicator_id,
  ];
  const availableUsersQueryKey = [
    "available-user-groups",
    entityInfo.bulk_communicator_id,
  ];
  const addedUsersQueryKey = [
    "added-user-groups",
    entityInfo.bulk_communicator_id,
  ];
  const allUserInfoQueryKey = [
    "user-management-type",
    entityInfo.bulk_communicator_id,
  ];
  const queryClient = useQueryClient();

  const { statusfailureReason, data: statusInfo } = useQuery({
    queryKey: queryKey,
    refetchInterval: (data) => {
      queryClient.getQueryData(pollingQueryKey);
      const pollingState = queryClient.getQueryData(pollingQueryKey);
      return pollingState ? 1000 : null;
    },
    queryFn: async () =>
      axios
        .post(
          `/bulk-communicators/${entityInfo.bulk_communicator_id}/data-grabber/`,
          {
            bulk_communicator_id: entityInfo.bulk_communicator_id,
            context: "user_management_polling",
          }
        )
        .then(({ data }) => {
          if (data.response[0].managing_users) {
            setLoading(true);
            queryClient.setQueryData(pollingQueryKey, true);
          } else {
            queryClient.setQueryData(pollingQueryKey, false);
            queryClient.invalidateQueries({ queryKey: availableUsersQueryKey });
            queryClient.invalidateQueries({ queryKey: addedUsersQueryKey });
            queryClient.invalidateQueries({ queryKey: allUserInfoQueryKey });
            queryClient.invalidateQueries({
              queryKey: [
                `bulkCommunication${entityInfo.bulk_communicator_id}Status`,
              ],
            });
            setLoading(false);
          }

          return data.response[0];
        })
        .catch((err) => {
          queryClient.setQueryData(pollingQueryKey, false);
          throw err;
        }),
  });

  const fetchUserAddData = async () => {
    setLoading(true);
    queryClient.setQueryData(pollingQueryKey, true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await queryClient.invalidateQueries({ queryKey: queryKey });
    fetchData();
  };

  async function onAddModeChange(value) {
    setLoading(true);
    queryClient.setQueryData(pollingQueryKey, true);
    await queryClient.invalidateQueries({ queryKey: queryKey });

    // Wait for polling to complete
    while (queryClient.getQueryData(pollingQueryKey)) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setAddMode(value);
  }

  return (
    <div>
      <Flex
        gap="xs"
        align="center"
        mb="sm"
        direction={{ base: "column", md: "row" }}
      >
        <Text fw={600} size="sm">
          Total possible users: {numberFormatter(totalPossibleUsers)}
        </Text>
        <AddModeSelector
          value={addMode}
          onSuccess={(newAddMode) => {
            onAddModeChange(newAddMode);
            fetchData();
          }}
          disabled={loading}
          setLoading={setLoading}
          reqData={{
            ...entityInfo,
          }}
        />
      </Flex>
      <Box>
        <div style={{ position: "relative" }}>
          <LoadingOverlay
            visible={loading}
            overlayProps={{ radius: "sm", blur: 2 }}
            loaderProps={{
              children: (
                <Flex
                  justify="center"
                  align="center"
                  gap="xs"
                  direction="column"
                >
                  <Loader />
                  <Text align="center">
                    {statusInfo?.message || "Loading..."}
                  </Text>
                </Flex>
              ),
            }}
          />
          <AnimatePresence mode="wait">
            {addMode === addModeInfo.groups.value && (
              <motion.div
                key="groups"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <Grid gap="lg">
                  <Grid.Col span={{ base: 12, md: 12 }}>
                    <Card>
                      <AvailableUsers
                        queryKey={availableUsersQueryKey}
                        reqData={{
                          ...entityInfo,
                        }}
                        fetchData={fetchUserAddData}
                        entityName={entityInfo.campaign_effort_title}
                      />
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 12 }}>
                    <Card>
                      <AddedUsers
                        queryKey={addedUsersQueryKey}
                        reqData={{
                          ...entityInfo,
                        }}
                        fetchData={fetchUserAddData}
                        entityName={entityInfo.campaign_effort_title}
                      />
                    </Card>
                  </Grid.Col>
                </Grid>
              </motion.div>
            )}
            {addMode === addModeInfo.all.value && (
              <motion.div
                key="all"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <Grid gap="lg">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <AllUserInfo
                      setLoading={setLoading}
                      fetchData={fetchUserAddData}
                      entityInfo={entityInfo}
                      queryKey={allUserInfoQueryKey}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card>
                      <Text fw={600}>Remove all users</Text>
                      <Text>
                        This will remove all users from the bulk communicator.
                        This may take a while.
                      </Text>
                      <Box mt="md">
                        <AllUserButton
                          setLoading={setLoading}
                          onSuccess={() => {
                            fetchUserAddData();
                          }}
                          reqData={{
                            ...entityInfo,
                          }}
                          isRemove
                        />
                      </Box>
                    </Card>
                  </Grid.Col>
                </Grid>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Box>
    </div>
  );
}

const groupInfo = {
  snapshots: {
    label: "Snapshots",
    description: "Snapshots description",
    value: "snapshots",
  },
  // saved_reports: {
  //   label: "Saved Reports",
  //   description: "Saved Reports description",
  //   value: "saved_reports",
  // },
  efforts: {
    label: "Efforts",
    description: "Efforts description",
    value: "efforts",
  },
  this_effort: {
    label: "Effort",
    description: "This Effort description",
    value: "this_effort",
  },
};

const groupFilterItems = Object.keys(groupInfo).map((key) => ({
  label: groupInfo[key].label,
  value: groupInfo[key].value,
}));

const AvailableUsers = ({ queryKey, reqData, fetchData, entityName }) => {
  const [filters, setFilters] = useState({
    entity: groupInfo.snapshots.value,
    effort: "registered",
    search_value: "",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [...queryKey, JSON.stringify(filters)],
    queryFn: async () => {
      try {
        const req = {
          ...reqData,
          context: filters.entity,
        };

        if (filters.entity === groupInfo.efforts.value) {
          req.effort_context = filters.effort;
          req.search_value = filters.search_value;
        }

        if (filters.entity === groupInfo.snapshots.value) {
          req.snapshots = true;
        }

        const { data } = await axios.post(
          filters.entity === groupInfo.this_effort.value
            ? `/segmentation/retrieve-effort-user-groups/`
            : filters.entity === groupInfo.efforts.value
            ? `/segmentation/retrieve-efforts-for-user-group/`
            : `/bulk-communicators/available-user-groups/`,
          req
        );

        if (filters.entity === groupInfo.this_effort.value) {
          const res = data.response[0];
          return {
            items: getEffortUserGroups(res),
            effortInfo: res,
          };
        }
        if (filters.entity === groupInfo.efforts.value) {
          return {
            items: data.response.map((m) => ({
              ...m,
              title: m.title,
              is_effort: true,
            })),
          };
        }
        return {
          items: data.response.map((m) => ({
            ...m,
            title: m.name || m.campaign_effort_title,
            date_text: `snapped ${new Date(m.created_at).toLocaleString()}`,
            user_group_id: m.id,
          })),
          effortInfo: null,
        };
      } catch (error) {
        // Re-throw the error so React Query can handle it properly
        throw error;
      }
    },
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });

  // Safely destructure the data with defaults
  const items = data?.items || [];
  const effortInfo = data?.effortInfo || null;
  const loading = isLoading || isFetching;

  return (
    <div>
      <Flex gap="xs" align="center">
        <Text fw={600}>Available Segments</Text>
      </Flex>
      <Text mb="xs" size="sm" c="dimmed">
        These are the segments that are available to add to the bulk
        communicator
      </Text>
      <Flex gap="xs">
        <GroupFilters
          activeFilter={filters.entity}
          setActiveFilter={(value) => setFilters({ ...filters, entity: value })}
          loading={loading}
          effortId={reqData.campaign_effort_id}
        />
        {/* {filters.entity === groupInfo.efforts.value && (
          <EffortFilters
            value={filters.effort}
            onChange={(value) => setFilters({ ...filters, effort: value })}
            loading={loading}
          />
        )} */}
      </Flex>
      <div style={{ position: "relative" }}>
        <LoadingOverlay visible={loading} />
        {effortInfo && (
          <Box mt="md">
            <EffortInfo
              effort={{
                ...effortInfo,
                title: entityName,
              }}
            />
          </Box>
        )}
        {filters.entity === groupInfo.efforts.value && (
          <TextInput
            placeholder="Search for efforts. Most recent displayed first."
            value={filters.search_value}
            mt="md"
            size="md"
            onChange={(e) =>
              setFilters({ ...filters, search_value: e.target.value })
            }
          />
        )}
        <GroupsWrapper
          connected={effortInfo ? true : false}
          visible={items.length > 0}
          added={false}
        >
          {items.map((userGroup, i) => (
            <GroupWrapper key={userGroup.id} index={i}>
              <UserGroup
                title={userGroup.title}
                item={userGroup}
                dateText={userGroup.date_text}
                description={userGroup.description}
                userCount={userGroup.user_id_count || userGroup.user_count}
                showRemove={false}
                fetchData={() => {
                  fetchData();
                }}
                entityName={entityName}
                reqData={{
                  ...reqData,
                  user_group_id: userGroup.user_group_id,
                  variety: userGroup.variety,
                }}
              />
            </GroupWrapper>
          ))}
        </GroupsWrapper>
      </div>
    </div>
  );
};

const AddedUsers = ({ queryKey, fetchData, reqData, entityName }) => {
  const [activeFilter, setActiveFilter] = useState("all");

  const { data, isLoading: loading } = useQuery({
    queryKey: [...queryKey, activeFilter],
    queryFn: async () => {
      try {
        const req = {
          ...reqData,
          context: activeFilter,
        };

        const { data } = await axios.post(
          `/bulk-communicators/added-user-groups/`,
          req
        );

        if (activeFilter === groupInfo.this_effort.value) {
          return {
            items: data.response.map((m) => ({
              ...m,
              // title: m.name || getEffortUserGroupTitle(m.variety),
              // description: m.description,
              title: m.description,
              description: "",
              group: "this_effort",
              user_group_id: m.id,
              // user_id_count: m.user_count,
            })),
            effortInfo: {
              title: entityName,
            },
          };
        }
        if (activeFilter === groupInfo.efforts.value) {
          return {
            items: data.response.map((m) => ({
              ...m,
              title: m.name,
              description: m.description,
              is_effort: true,
              user_group_id: m.id,
              group: "efforts",
            })),
          };
        }
        if (activeFilter === groupInfo.snapshots.value) {
          return {
            items: data.response.map((m) => ({
              ...m,
              title: m.name || m.campaign_effort_title,
              description: m.description,
              date_text: `snapped ${new Date(m.created_at).toLocaleString()}`,
              group: "snapshots",
              user_group_id: m.id,
            })),
          };
        }
        return {
          items: data.response.map((m) => ({
            ...m,
            title: m.name || m.campaign_effort_title,
            description: m.description,
            date_text: `${new Date(m.created_at).toLocaleString()}`,
            group: "snapshots",
            user_group_id: m.id,
          })),
        };
      } catch (error) {
        // Re-throw the error so React Query can handle it properly
        throw error;
      }
    },
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });

  // Safely get items with default
  const items = data?.items || [];
  const effortInfo = data?.effortInfo || null;

  const filteredGroups = items.filter((f) =>
    activeFilter === "all" ? true : f.group === activeFilter
  );

  return (
    <div>
      <Flex gap="xs" align="center">
        <Text fw={600}>
          Audience
          {items.length > 0 &&
            ` (${items.length} segment${items.length === 1 ? "" : "s"})`}
        </Text>
      </Flex>
      <Text mb="xs" size="sm" c="dimmed">
        These are the segments that are currently added to the bulk communicator
      </Text>
      <GroupFilters
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        showAll
        effortId={reqData.campaign_effort_id}
      />
      <div style={{ position: "relative" }}>
        <LoadingOverlay visible={loading} />
        {effortInfo && (
          <Box mt="md">
            <EffortInfo
              effort={{
                title: entityName,
              }}
            />
          </Box>
        )}
        <GroupsWrapper
          visible={filteredGroups.length > 0}
          added={true}
          connected={effortInfo ? true : false}
        >
          {filteredGroups.map((userGroup, i) => (
            <GroupWrapper key={userGroup.id} index={i} added>
              <UserGroupItem
                original={{
                  ...userGroup,
                  user_count: userGroup.user_count || userGroup.user_id_count,
                }}
                fetchData={() => {
                  fetchData();
                }}
                entityData={{
                  campaign_effort_id: reqData.campaign_effort_id,
                }}
              >
                <AddRemoveButtons
                  reqData={{
                    ...reqData,
                    user_group_id: userGroup.user_group_id,
                  }}
                  fetchData={() => {
                    fetchData();
                  }}
                  added={true}
                  title={userGroup.title}
                />
              </UserGroupItem>
            </GroupWrapper>
          ))}
        </GroupsWrapper>
      </div>
    </div>
  );
};

const GroupFilters = ({
  activeFilter,
  setActiveFilter,
  loading,
  showAll = false,
  effortId,
}) => {
  const formattedFilters = [
    {
      label: "All",
      value: "all",
    },
    ...groupFilterItems,
  ]
    .filter((f) => (showAll ? true : f.value !== "all"))
    .filter((f) =>
      effortId ? f.value !== "efforts" : f.value !== "this_effort"
    );

  return (
    <SegmentedControl
      value={activeFilter}
      onChange={setActiveFilter}
      disabled={loading}
      size="xs"
      radius="xl"
      data={formattedFilters}
    />
  );
};

export const GroupsWrapper = ({
  children,
  visible,
  added,
  connected = false,
}) => {
  if (!visible)
    return (
      <Box mt="md" p="sm">
        <Text fw={600} size="sm">
          No items
        </Text>
      </Box>
    );

  return (
    <Box
      mt={connected ? 0 : "md"}
      style={{
        maxHeight: "600px",
        overflowY: "auto",
        border: `1px solid ${
          added ? "var(--mantine-color-green-2)" : "var(--mantine-color-gray-2)"
        }`,
        borderRadius: connected
          ? "0 0 var(--mantine-radius-md) var(--mantine-radius-md)"
          : "var(--mantine-radius-md)",
      }}
    >
      {children}
    </Box>
  );
};

export const GroupWrapper = ({ children, index, added }) => {
  return (
    <Box
      p="xs"
      style={{
        background: added
          ? index % 2 === 0
            ? "var(--mantine-color-green-0)"
            : "var(--mantine-color-green-1)"
          : index % 2 === 0
          ? "var(--mantine-color-gray-0)"
          : "white",
      }}
    >
      {children}
    </Box>
  );
};

const AllUserButton = ({ setLoading, onSuccess, reqData, isRemove }) => {
  const [confirm, setConfirm] = useState(false);

  function onClick() {
    if (isRemove && !confirm) {
      setConfirm(true);
      return;
    }

    setLoading(true);

    axios
      .post(
        isRemove
          ? `/bulk-communicators/remove-user-group/`
          : `/bulk-communicators/add-user-group/`,
        {
          ...reqData,
          all_users: true,
        }
      )
      .then(({ data }) => {
        setConfirm(false);
        onSuccess();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  if (confirm) {
    return (
      <Box>
        <Text>Are you sure you want to remove all users?</Text>
        <Flex mt="md" gap="xs">
          <Button onClick={onClick} color="red">
            Remove all users
          </Button>
          <Button onClick={() => setConfirm(false)} variant="outline">
            Cancel
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Button onClick={onClick} color={isRemove ? "red" : "green"} size="sm">
      {isRemove ? "Remove all users" : "Add all users"}
    </Button>
  );
};

const AddModeSelector = ({
  value,
  onSuccess,
  disabled,
  setLoading,
  reqData,
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  const handleChange = (newValue) => {
    setPendingValue(newValue);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    setLoading(true);

    axios
      .post(`/bulk-communicators/remove-user-group/`, {
        ...reqData,
        all_users: true,
        users_toggle: true,
        user_management: pendingValue,
      })
      .then(({ data }) => {
        onSuccess(pendingValue);
        setShowConfirmModal(false);
        setPendingValue(null);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  };

  return (
    <>
      <SegmentedControl
        radius="xl"
        disabled={disabled}
        size="sm"
        value={value}
        onChange={handleChange}
        data={Object.values(addModeInfo).map((m) => ({
          label: m.label,
          value: m.value,
        }))}
      />
      <Modal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Mode Change"
        centered
      >
        <Text mb="md">
          Are you sure you want to switch? This will reset the users.
        </Text>
        <Flex justify="flex-end" gap="xs">
          <Button variant="default" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Confirm</Button>
        </Flex>
      </Modal>
    </>
  );
};

const AllUserInfo = ({ setLoading, fetchData, entityInfo, queryKey }) => {
  const queryClient = useQueryClient();
  const { data: info } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        const { data } = await axios.post(
          `/bulk-communicators/${entityInfo.bulk_communicator_id}/data-grabber/`,
          {
            bulk_communicator_id: entityInfo.bulk_communicator_id,
            context: "user_management_type",
          }
        );
        return data.response[0];
      } catch (error) {
        // Re-throw the error so React Query can handle it properly
        throw error;
      }
    },
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });

  if (!info) return null;

  if (info.all_users_added) {
    return (
      <div>
        <Flex gap="xs" align="center">
          <Text fw={600}>All users added</Text>
          <ThemeIcon color="green" size="xs" radius="xl" variant="light">
            <IconCheck size={12} />
          </ThemeIcon>
        </Flex>
        <Text>Users added: {info.user_count}</Text>
      </div>
    );
  }

  return (
    <Card>
      <Text fw={600}>Add all users</Text>
      <Text>
        This will add all users to the bulk communicator. This may take a while.
      </Text>
      <Box mt="md">
        <AllUserButton
          setLoading={setLoading}
          onSuccess={() => {
            fetchData();
          }}
          reqData={{
            ...entityInfo,
          }}
        />
      </Box>
    </Card>
  );
};

export const EffortInfo = ({ effort }) => {
  const [loading, setLoading] = useState(false);

  function onUpdateClick() {
    setLoading(true);
  }

  const badges = [
    { label: "Started", value: effort.started },
    { label: "In Progress", value: effort.in_progress },
    { label: "Completed", value: effort.completed },
  ].filter((f) => f.value);
  return (
    <div
      style={{
        position: "relative",
        background: "var(--mantine-color-orange-0)",
        padding: "var(--mantine-spacing-sm)",
        borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
        border: "1px solid var(--mantine-color-orange-2)",
      }}
    >
      <LoadingOverlay visible={loading} />
      <Flex gap="xs" align="center">
        {/* <ThemeIcon
          color={entityColor.effort}
          size="md"
          radius="xl"
          variant="light"
        >
          {entityIcon.effort(16)}
        </ThemeIcon> */}
        <div style={{ flexGrow: 1 }}>
          <Flex
            gap="xs"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
          >
            <Text size="sm" fw={600}>
              {effort.title}
            </Text>
            {badges && badges.length > 0 && (
              <Flex gap="xs" mt={5}>
                {badges.map((badge, i) => (
                  <Badge key={i} color="orange" variant="light" size="sm">
                    {badge.label}
                  </Badge>
                ))}
              </Flex>
            )}
          </Flex>
        </div>
      </Flex>
    </div>
  );
};
